import * as XLSX from 'xlsx';

/**
 * Export table data to Excel file
 * @param {Array} data - Array of record objects
 * @param {Array} columns - Array of column definitions
 * @param {Array} selectedRows - Array of selected row IDs (optional)
 * @param {string} filename - Name of the Excel file
 * @param {string} sheetName - Name of the worksheet
 */
export const exportToExcel = (data, columns, selectedRows = [], filename = 'export', sheetName = 'Sheet1') => {
  try {
    // Normalize record id value (supports primitive or { value })
    const getRecordIdValue = (record) => {
      if (!record) return '';
      if (record.id && typeof record.id === 'object' && record.id !== null) {
        return record.id.value ?? '';
      }
      return record.id ?? '';
    };

    // Filter data if selectedRows is provided
    const exportData = selectedRows.length > 0 
      ? data.filter(record => selectedRows.includes(getRecordIdValue(record)))
      : data;

    if (exportData.length === 0) {
      alert('No data to export!');
      return;
    }

    const exportColumns = columns.some(column => column.field === 'id')
      ? columns
      : [{ field: 'id', label: 'ID' }, ...columns];

    // Prepare headers
    const headers = exportColumns.map(column => column.label || column.field);

    // Prepare rows
    const rows = exportData.map(record => {
      const row = {};
      exportColumns.forEach(column => {
        const field = record[column.field];
        if (column.field === 'id') {
          row[column.label || column.field] = getRecordIdValue(record);
        } else if (field) {
          // Handle field objects with value/displayValue
          if (typeof field === 'object' && field.value !== undefined) {
            row[column.label || column.field] = field.displayValue || field.value;
          } else {
            row[column.label || column.field] = field;
          }
        } else {
          row[column.label || column.field] = '';
        }
      });
      return row;
    });

    // Create workbook and worksheet
    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.json_to_sheet(rows);

    // Set column widths
    const columnWidths = exportColumns.map(column => ({
      wch: Math.max(
        (column.label || column.field).length,
        ...rows.map(row => String(row[column.label || column.field] || '').length)
      )
    }));
    worksheet['!cols'] = columnWidths;

    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);

    // Generate filename with timestamp
    const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
    const fullFilename = `${filename}_${timestamp}.xlsx`;

    // Save file
    XLSX.writeFile(workbook, fullFilename);

    console.log(`Excel file exported successfully: ${fullFilename}`);
    return fullFilename;
  } catch (error) {
    console.error('Error exporting to Excel:', error);
    alert('Error exporting to Excel. Please try again.');
  }
};

/**
 * Export table data to CSV file (alternative to Excel)
 * @param {Array} data - Array of record objects
 * @param {Array} columns - Array of column definitions
 * @param {Array} selectedRows - Array of selected row IDs (optional)
 * @param {string} filename - Name of the CSV file
 */
export const exportToCSV = (data, columns, selectedRows = [], filename = 'export') => {
  try {
    // Filter data if selectedRows is provided
    const exportData = selectedRows.length > 0 
      ? data.filter(record => selectedRows.includes(record.id))
      : data;

    if (exportData.length === 0) {
      alert('No data to export!');
      return;
    }

    const exportColumns = columns.some(column => column.field === 'id')
      ? columns
      : [{ field: 'id', label: 'ID' }, ...columns];

    // Prepare headers
    const headers = exportColumns.map(column => column.label || column.field);

    // Prepare rows
    const rows = exportData.map(record => {
      return exportColumns.map(column => {
        const field = record[column.field];
        if (column.field === 'id') {
          return `"${record?.id?.value ?? record?.id ?? ''}"`;
        }
        if (field) {
          // Handle field objects with value/displayValue
          if (typeof field === 'object' && field.value !== undefined) {
            return `"${field.displayValue || field.value}"`;
          } else {
            return `"${field}"`;
          }
        }
        return '""';
      });
    });

    // Create CSV content
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');

    // Create and download file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
    const fullFilename = `${filename}_${timestamp}.csv`;
    
    link.href = URL.createObjectURL(blob);
    link.download = fullFilename;
    link.click();

    console.log(`CSV file exported successfully: ${fullFilename}`);
    return fullFilename;
  } catch (error) {
    console.error('Error exporting to CSV:', error);
    alert('Error exporting to CSV. Please try again.');
  }
};
